import { useEffect } from "react";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import CareerManagersService from "../services/api/careerManagers/CareerManagers.service";
import { useQuery } from "@tanstack/react-query";
import { useNotification } from "./notification.hook";
import {
  CAREER_MANAGERS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import CareerManagersFollowedService from "../services/api/careerManagers/CareerManagersFollowed.service";

interface useCareerManagersFollowedProps {
  careerManagerId: string | undefined;
  pageNumber: number;
  searchDebouncedValue: string;
}

export const useCareerManagersFollowed = ({
  careerManagerId,
  pageNumber,
  searchDebouncedValue,
}: useCareerManagersFollowedProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      `get-career-manager-followed-collaborators-${careerManagerId}`,
      pageNumber,
      searchDebouncedValue,
    ],
    queryFn: () =>
      CareerManagersFollowedService.list(
        careerManagerId!,
        CAREER_MANAGERS_PAGE_SIZE,
        pageNumber,
        [
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useCareerManager = ({
  careerManagerId,
}: {
  careerManagerId: string | undefined;
}) => {
  const { notification } = useNotification();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [`get-career-manager-details-${careerManagerId?.toString()!}`],
    queryFn: () => CareerManagersService.get(careerManagerId?.toString()!),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};
